<template>
  <EditorModal
    :title="modalTitle"
    :toolbar-right-buttons="toolbarRightButtons"
    :content-header-toolbar-start="contentHeaderToolbarStart"
    :content-header-toolbar-end="contentHeaderToolbarEnd"
    :tabs="navigationTabs"
    @on-close-modal="handleClose"
  >
    <template
      v-if="isBulkFlow"
      #content-header-start-extra
    >
      <!-- Edit mode: interactive toggle -->
      <div
        v-if="!isPreviewMode"
        class="commercial-toggle-wrapper"
      >
        <InputSwitch
          :model-value="isCommercialActive"
          input-id="flowEditorCommercialCampaign"
          @update:model-value="onCommercialSwitchChange"
        />
        <label
          for="flowEditorCommercialCampaign"
          class="commercial-toggle-wrapper__label"
        >
          {{ t('flows.toolbar.commercialCampaign.label') }}
        </label>
        <i
          v-tooltip.top="t('flows.toolbar.commercialCampaign.tooltip')"
          class="far fa-circle-question commercial-toggle-wrapper__help"
        />
      </div>

      <!-- Preview mode: read-only badge with current state -->
      <div
        v-else
        class="commercial-readonly-badge"
        :class="isCommercialActive
          ? 'commercial-readonly-badge--on'
          : 'commercial-readonly-badge--off'"
      >
        <i :class="isCommercialActive ? 'far fa-check-circle' : 'far fa-times-circle'" />
        <span>{{ t('flows.toolbar.commercialCampaign.label') }}</span>
        <i
          v-tooltip.top="t('flows.toolbar.commercialCampaign.tooltip')"
          class="far fa-circle-question commercial-readonly-badge__help"
        />
      </div>
    </template>

    <template #content>
      <div class="flow-editor-modal__content">
        <div
          v-if="loading"
          class="flow-editor-modal__loading"
        >
          <div class="flow-editor-modal__loading-spinner">
            <i class="far fa-spinner-third fa-spin" />
          </div>
          <div class="flow-editor-modal__loading-text">
            {{ t('flows.loading.title') }}
          </div>
          <div class="flow-editor-modal__loading-subtitle">
            {{ t('flows.loading.subtitle') }}
          </div>
        </div>
        <FlowRenderer
          v-else-if="flow"
          ref="flowRendererRef"
          :flow="flow"
          :type="flowType"
          @add-operator="handleAddOperator"
          @update-step="handleUpdateStep"
          @delete-step="handleDeleteStep"
          @decrease-branches="handleDecreaseBranches"
          @replace-step="handleReplaceStep"
          @menu-action="handleMenuAction"
          @start-flow="handleStartFlow"
          @open-settings="openSettingsPanel"
          @merge-branch="handleMergeBranch"
          @reorder-branches="handleReorderBranches"
          @remove-connection="handleRemoveConnection"
        />
        <div
          v-else
          class="flow-editor-modal__state"
        >
          <i class="far fa-diagram-project" />
          <span>{{ t('flows.emptyState.noFlow') }}</span>
        </div>

        <!-- Analytics Loading Overlay -->
        <Transition name="fade">
          <div
            v-if="flowAnalytics.isLoading.value"
            class="flow-editor-modal__analytics-loading"
          >
            <div class="flow-editor-modal__analytics-loading-pill">
              <i class="far fa-spinner-third fa-spin" />
              <span>{{ t('campaigns.common.operatorAnalytics.loading') }}</span>
            </div>
          </div>
        </Transition>

        <!-- Settings Panel -->
        <FlowSettingsPanel
          v-if="activeFlowIdentifier"
          ref="settingsPanelRef"
          :is-open="isSettingsPanelOpen"
          :settings="flowSettings"
          :flow-id="activeFlowIdentifier.id"
          :flow-type="activeFlowIdentifier.type"
          @close="closeSettingsPanel"
        />

        <!-- Analytics Custom Date Range Picker -->
        <div
          v-if="showCustomDatePicker"
          class="flow-editor-modal__custom-date-picker"
        >
          <div class="flow-editor-modal__custom-date-picker-header">
            <span>{{ t('customDatePicker.empty') }}</span>
            <button
              class="flow-editor-modal__custom-date-picker-close"
              @click="closeCustomDatePicker"
            >
              <i class="far fa-times" />
            </button>
          </div>
          <Calendar
            v-model="customDateRange"
            selection-mode="range"
            :hide-on-range-selection="true"
            :manual-input="false"
            date-format="dd/mm/yy"
            :max-date="new Date()"
            :inline="true"
            @date-select="onCustomDateSelect"
          />
        </div>
      </div>
      <!-- Send Test Modal (V3 email or non-email types: SMS, WhatsApp, Push) -->
      <SendTestModal
        v-if="showSendTestModal"
        :type-of-input="sendTestTemplateType"
        @on-validate="sendTestTemplate"
        @on-close-dialog="closeSendTestModal"
      />

      <!-- Unified Send Test V4 modal (email V4 + sms) -->
      <SendTestV4Modal
        v-if="isSendTestV4ModalVisible && ['email', 'sms', 'whatsapp', 'richtexting'].includes(sendTestTemplateType)"
        :channel="sendTestTemplateType"
        :rcs-sender-id="sendTestStep?.parameters?.rcs_sender_id || ''"
        @on-validate="sendTestTemplateV4"
      />

      <!-- Schedule Bulk Modal -->
      <FlowScheduleBulkModal
        :visible="isScheduleModalOpen"
        :loading="loading"
        @close="closeScheduleModal"
        @schedule="handleSchedule"
      />

      <!-- Confirmation Dialog for blocking warnings -->
      <ConfirmDialog group="flowEditorConfirm" />

      <!-- History Panel -->
      <FlowHistoryPanel
        :visible="isHistoryPanelOpen"
        :undo-stack="undoStack"
        :redo-stack="redoStack"
        @close="closeHistoryPanel"
        @restore="handleHistoryRestore"
      />
    </template>
  </EditorModal>
</template>

<script lang="ts">
import {
  computed, defineComponent, ref, onMounted, onBeforeUnmount, nextTick, watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from '@/store';
import { useConfirm } from 'primevue/useconfirm';
import { usePrimeVue } from 'primevue/config';
import { TypeCampaignEnum, CampaignsBulkStatusEnum, CampaignAutomationStatusEnum, IntervalDateEnum } from '@/types';
import { isDemoModeActive } from '@/composables/DemoMode';
import { isWorkshopAccount } from '@/composables/WorkshopMode';
import { buildTemplateRenamePreview, executeTemplateRenames } from '@/composables/flows/useWorkshopActions';
import { NestApiType, nestCall } from '@/composables/nestApi';
import { UserState } from '@/composables/User';
import { showToastSuccess, showToastError, truncateLabel } from '@/helpers';
import EditorModal from '@/components/modals/EditorModal.vue';
import FlowRenderer from '@/components/flows/FlowRenderer.vue';
import SendTestModal from '@/components/modals/SendTestModal.vue';
import SendTestV4Modal from '@/components/modals/SendTestV4Modal.vue';
import FlowSettingsPanel from '@/components/flows/settings/FlowSettingsPanel.vue';
import FlowScheduleBulkModal from '@/components/flows/FlowScheduleBulkModal.vue';
import FlowHistoryPanel from '@/components/flows/FlowHistoryPanel.vue';
import ConfirmDialog from 'primevue/confirmdialog';
import Calendar from 'primevue/calendar';
import InputSwitch from 'primevue/inputswitch';
import Tooltip from 'primevue/tooltip';
import { useFlowEditor, AddOperatorPayload } from '@/composables/flows/useFlowEditor';
import type { HistoryEntryMeta } from '@/composables/flows/useFlowHistory';
import { formatHistoryMetaLabel } from '@/composables/flows/useFlowEditor';
import { useGlobalFlowAnalytics, resetGlobalFlowAnalytics } from '@/composables/flows/useFlowAnalytics';
import {
  updateBulkStatus,
  saveBulk,
  saveAutomation,
  scheduleBulk,
  saveFlowHistory,
} from '@/composables/flows/useFlowApi';
import { processFlowTriggersBeforeSave } from '@/composables/flows/useFlowTriggers';
import { flowHasSendingAction, flowHasOperators } from '@/components/flows/operators/registry';
import type { FlowStructureType, FlowStructureDto } from '@/types/flow-structure';
import type { FlowSettings, FlowSettingsBulk } from '@/types/flow-settings';

export default defineComponent({
  name: 'FlowEditorModal',

  components: {
    EditorModal,
    FlowRenderer,
    SendTestModal,
    SendTestV4Modal,
    FlowSettingsPanel,
    FlowScheduleBulkModal,
    FlowHistoryPanel,
    ConfirmDialog,
    Calendar,
    InputSwitch,
  },

  directives: {
    tooltip: Tooltip,
  },

  setup() {
    const { t, locale } = useI18n();
    const store = useStore();
    const confirm = useConfirm();
    const primevue = usePrimeVue();
    const flowRendererRef = ref<any>(null);
    const loading = ref(false);
    const isSettingsPanelOpen = ref(false);
    const isScheduleModalOpen = ref(false);
    const isHistoryPanelOpen = ref(false);

    // Configure PrimeVue Calendar locale
    const configureCalendarLocale = () => {
      if (locale.value === 'fr') {
        primevue.config.locale.dayNames = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
        primevue.config.locale.dayNamesShort = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
        primevue.config.locale.dayNamesMin = ['Di', 'Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa'];
        primevue.config.locale.monthNames = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
        primevue.config.locale.monthNamesShort = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
        primevue.config.locale.today = 'Aujourd\'hui';
        primevue.config.locale.clear = 'Effacer';
        primevue.config.locale.firstDayOfWeek = 1;
      }
    };

    // Analytics mode
    const flowAnalytics = useGlobalFlowAnalytics();
    const customDateRange = ref<Date[]>([]);
    const showCustomDatePicker = ref(false);

    // Format date for display (dd/mm)
    const formatDateShort = (date: Date): string => {
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      return `${day}/${month}`;
    };

    // Handle custom date range selection
    const onCustomDateSelect = async () => {
      if (customDateRange.value && customDateRange.value.length === 2) {
        // Convert to proper Date objects using getTime()
        const dates = customDateRange.value.map((d: any) => new Date(d.getTime()));
        flowAnalytics.setDateInterval(IntervalDateEnum.CUSTOM_DATE_RANGE, dates);
        // Close the calendar after selection
        showCustomDatePicker.value = false;
        // Reload analytics data with custom date range
        const idShop = UserState.activeShop?.id ?? 0;
        const idCampaign = activeFlowIdentifier.value?.id ?? 0;
        const hasData = await flowAnalytics.loadAnalyticsData(
          idShop,
          TypeCampaignEnum.AUTOMATION,
          idCampaign,
        );
        if (!hasData) {
          showToastError(t('campaigns.common.operatorAnalytics.noAnalyticsForThisInterval'));
        }
      }
    };

    const activeFlowIdentifier = computed(() => store.getters['flowEditor/getActiveFlowIdentifier']);
    const activeFlow = computed(() => store.getters['flowEditor/getActiveFlow']);
    const allFlows = computed(() => store.getters['flowEditor/getOpenFlowsList']);

    // Preview mode - analytics only, no editing allowed
    const isPreviewMode = computed(() => activeFlow.value?.isPreviewMode ?? false);

    const flowEditor = computed(() => {
      if (!activeFlowIdentifier.value) return null;
      return useFlowEditor(activeFlowIdentifier.value);
    });

    const flow = computed(() => flowEditor.value?.flow.value ?? null);

    // History state
    const canUndo = computed(() => flowEditor.value?.canUndo.value ?? false);
    const canRedo = computed(() => flowEditor.value?.canRedo.value ?? false);
    const undoActionType = computed(() => flowEditor.value?.undoActionType.value ?? undefined);
    const undoMeta = computed(() => flowEditor.value?.undoMeta.value ?? undefined);
    const redoActionType = computed(() => flowEditor.value?.redoActionType.value ?? undefined);
    const redoMeta = computed(() => flowEditor.value?.redoMeta.value ?? undefined);
    const undoStack = computed(() => flowEditor.value?.undoStack.value ?? []);
    const redoStack = computed(() => flowEditor.value?.redoStack.value ?? []);
    // Get snapshot from last undo/redo entry for tooltip display
    const undoSnapshot = computed(() => {
      const stack = undoStack.value;
      return stack.length > 0 ? stack[stack.length - 1].snapshot : undefined;
    });
    const redoSnapshot = computed(() => {
      const stack = redoStack.value;
      return stack.length > 0 ? stack[stack.length - 1].snapshot : undefined;
    });

    const flowType = computed<FlowStructureType>(() => {
      if (!activeFlowIdentifier.value) return TypeCampaignEnum.AUTOMATION;
      return activeFlowIdentifier.value.type === TypeCampaignEnum.BULK ? TypeCampaignEnum.BULK : TypeCampaignEnum.AUTOMATION;
    });

    const modalTitle = computed(() => {
      if (!activeFlow.value) return '';
      return activeFlow.value.name || `Campagne n°${activeFlow.value.id}`;
    });

    const modalSubtitle = computed(() => {
      if (!activeFlowIdentifier.value) return '';
      return activeFlowIdentifier.value.type === TypeCampaignEnum.BULK
        ? t('flows.editor.subtitleBulk')
        : t('flows.editor.subtitleAutomation');
    });

    // Settings panel
    const settingsPanelRef = ref<any>(null);
    const flowSettings = computed<FlowSettings | null>(() => activeFlow.value?.settings ?? null);

    // Bulk-only: commercial campaign toggle (also exposed in the toolbar)
    const isBulkFlow = computed(
      () => activeFlowIdentifier.value?.type === TypeCampaignEnum.BULK,
    );
    const isCommercialActive = computed(() => {
      if (!isBulkFlow.value || !flowSettings.value) return false;
      return Number((flowSettings.value as FlowSettingsBulk).commercial_campaign) === 1;
    });

    const setCommercialCampaign = (value: 0 | 1) => {
      if (!activeFlowIdentifier.value) return;
      store.commit('flowEditor/updateFlowSettings', {
        id: activeFlowIdentifier.value.id,
        type: activeFlowIdentifier.value.type,
        settings: { commercial_campaign: value },
      });
    };

    // InputSwitch handler: receives the proposed new boolean value.
    // The switch is controlled (`:model-value`) so if we don't commit the change
    // (e.g. user cancels the confirm dialog), the visual state automatically
    // reverts to `isCommercialActive`.
    const onCommercialSwitchChange = (newValue: boolean) => {
      if (!isBulkFlow.value) return;
      if (newValue) {
        setCommercialCampaign(1);
        return;
      }
      // newValue === false → confirm before disabling
      // Must use the same `group` as the <ConfirmDialog group="flowEditorConfirm" />
      // declared in this component's template, otherwise the dialog never shows.
      confirm.require({
        group: 'flowEditorConfirm',
        message: t('automatedScenarios.globalBulkSettings.commercial_campaign.dialogConfirm.message'),
        header: 'Confirmation',
        icon: 'far fa-exclamation-triangle',
        acceptLabel: t('yes'),
        rejectLabel: t('no'),
        accept: () => setCommercialCampaign(0),
        // reject: noop — the controlled InputSwitch reverts on its own
      });
    };

    const openSettingsPanel = () => {
      isSettingsPanelOpen.value = true;
    };

    const closeSettingsPanel = () => {
      isSettingsPanelOpen.value = false;
    };

    // History handlers
    const handleUndo = () => {
      flowEditor.value?.undo();
    };

    const handleRedo = () => {
      flowEditor.value?.redo();
    };

    // Keyboard shortcuts for undo/redo
    const handleKeydown = (event: KeyboardEvent) => {
      // Check if we're in an input field - don't intercept shortcuts there
      const target = event.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return;
      }

      // Ctrl+Z or Cmd+Z for undo
      if ((event.ctrlKey || event.metaKey) && event.key === 'z' && !event.shiftKey) {
        event.preventDefault();
        handleUndo();
      }
      // Ctrl+Shift+Z or Cmd+Shift+Z for redo
      else if ((event.ctrlKey || event.metaKey) && event.key === 'z' && event.shiftKey) {
        event.preventDefault();
        handleRedo();
      }
      // Ctrl+Y or Cmd+Y for redo (alternative)
      else if ((event.ctrlKey || event.metaKey) && event.key === 'y') {
        event.preventDefault();
        handleRedo();
      }
    };

    onMounted(() => {
      window.addEventListener('keydown', handleKeydown);
      configureCalendarLocale();
    });

    // Watch for preview mode changes to auto-enable analytics mode
    watch(isPreviewMode, async (newValue) => {
      if (newValue && !flowAnalytics.isAnalyticsMode.value) {
        flowAnalytics.enableAnalyticsMode();
        // Load analytics data for preview mode
        const idShop = UserState.activeShop?.id ?? 0;
        const idCampaign = activeFlowIdentifier.value?.id ?? 0;
        const typeCampaign = activeFlowIdentifier.value?.type === TypeCampaignEnum.BULK 
          ? TypeCampaignEnum.BULK 
          : TypeCampaignEnum.AUTOMATION;
        const hasData = await flowAnalytics.loadAnalyticsData(
          idShop,
          typeCampaign,
          idCampaign,
        );
        if (!hasData) {
          showToastError(t('campaigns.common.operatorAnalytics.noAnalyticsForThisInterval'));
        }
      }
    }, { immediate: true });

    onBeforeUnmount(() => {
      window.removeEventListener('keydown', handleKeydown);
    });

    const handleMinimize = async () => {
      if (!activeFlowIdentifier.value) return;

      // Reset status to DRAFT for bulk campaigns (skip in preview mode)
      if (activeFlowIdentifier.value.type === TypeCampaignEnum.BULK && !isPreviewMode.value) {
        try {
          await updateBulkStatus(activeFlowIdentifier.value.id, CampaignsBulkStatusEnum.DRAFT);
          store.commit('flowEditor/updateFlowStatus', {
            id: activeFlowIdentifier.value.id,
            type: activeFlowIdentifier.value.type,
            status: CampaignsBulkStatusEnum.DRAFT,
          });
        } catch (error) {
          console.error('Failed to update bulk status to DRAFT:', error);
        }
      }

      store.dispatch('flowEditor/minimizeFlow', activeFlowIdentifier.value);
      store.commit('flowEditor/setEditorVisible', false);
    };

    const handleClose = async () => {
      if (!activeFlowIdentifier.value) return;

      // Reset analytics mode state
      resetGlobalFlowAnalytics();
      showCustomDatePicker.value = false;

      // Reset status to DRAFT for bulk campaigns (skip in preview mode)
      if (activeFlowIdentifier.value.type === TypeCampaignEnum.BULK && !isPreviewMode.value) {
        try {
          await updateBulkStatus(activeFlowIdentifier.value.id, CampaignsBulkStatusEnum.DRAFT);
          store.commit('flowEditor/updateFlowStatus', {
            id: activeFlowIdentifier.value.id,
            type: activeFlowIdentifier.value.type,
            status: CampaignsBulkStatusEnum.DRAFT,
          });
        } catch (error) {
          console.error('Failed to update bulk status to DRAFT:', error);
        }
      }

      store.dispatch('flowEditor/closeFlow', activeFlowIdentifier.value);
    };

    const performSave = async (closeAfterSave = false) => {
      if (!activeFlowIdentifier.value || loading.value) return;

      try {
        loading.value = true;

        if (!flow.value || !flowSettings.value) {
          await showToastError(t('flows.save.error'));
          return;
        }

        // Handle bulk campaigns
        if (activeFlowIdentifier.value.type === TypeCampaignEnum.BULK) {
          const result = await saveBulk(flow.value, flowSettings.value);

          if (result.success) {
            // Update status to DRAFT after successful save
            try {
              await updateBulkStatus(activeFlowIdentifier.value.id, CampaignsBulkStatusEnum.DRAFT);
              store.commit('flowEditor/updateFlowStatus', {
                id: activeFlowIdentifier.value.id,
                type: activeFlowIdentifier.value.type,
                status: CampaignsBulkStatusEnum.DRAFT,
              });
            } catch (statusError) {
              console.error('Failed to update bulk status to DRAFT after save:', statusError);
            }

            // Create save checkpoint in history and save to database
            flowEditor.value?.createSaveCheckpoint();

            // Get history from store and save to database
            const bulkFlowData = store.getters['flowEditor/getFlowById'](activeFlowIdentifier.value.id, activeFlowIdentifier.value.type);
            if (bulkFlowData?.history) {
              const bulkHistory = [...(bulkFlowData.history.undoStack || []), ...(bulkFlowData.history.redoStack || [])];
              if (bulkHistory.length > 0) {
                saveFlowHistory(TypeCampaignEnum.BULK, activeFlowIdentifier.value.id, bulkHistory).catch((historyError) => {
                  console.error('Failed to save flow history:', historyError);
                });
              }
            }

            await showToastSuccess(t('flows.save.success'));
            store.dispatch('flowEditor/markFlowAsSaved', activeFlowIdentifier.value);
          } else {
            await showToastError(t('flows.save.error'));
          }
        } else if (activeFlowIdentifier.value.type === TypeCampaignEnum.AUTOMATION) {
          // Handle automation campaigns
          const oldId = activeFlowIdentifier.value.id;
          const campaignName = flowSettings.value.name;
          const currentStatus = flow.value.status;

          // Determine the status based on blocking warnings
          const hasBlockingWarnings = flowRendererRef.value?.hasBlockingWarnings?.() ?? false;
          let newStatus: CampaignAutomationStatusEnum;
          if (hasBlockingWarnings) {
            // If there are inconsistencies, status must be draft
            newStatus = CampaignAutomationStatusEnum.DRAFT;
          } else if (currentStatus === CampaignAutomationStatusEnum.ACTIVE) {
            // If no inconsistencies and was active, keep active
            newStatus = CampaignAutomationStatusEnum.ACTIVE;
          } else {
            // Otherwise, set to disabled
            newStatus = CampaignAutomationStatusEnum.DISABLED;
          }

          const isActive = newStatus === CampaignAutomationStatusEnum.ACTIVE;

          // Process triggers (create events, webhooks, periodic configurations)
          const processedFlow = await processFlowTriggersBeforeSave(flow.value, campaignName, isActive);

          // Update flow status
          processedFlow.status = newStatus;

          // Update flow in store if steps were modified (webhooks added) or status changed
          if (processedFlow !== flow.value || newStatus !== currentStatus) {
            store.commit('flowEditor/updateFlowData', {
              id: activeFlowIdentifier.value.id,
              type: activeFlowIdentifier.value.type,
              flowData: processedFlow,
            });
          }

          const result = await saveAutomation(processedFlow, flowSettings.value);

          if (result.success) {
            // Check if the campaign ID has changed (new version created)
            const newId = result.campaignId;
            if (result.isNewVersion) {
              // Update the flow ID in the store
              store.dispatch('flowEditor/updateFlowIdAfterSave', {
                oldId,
                newId,
                type: TypeCampaignEnum.AUTOMATION,
              });
            }

            // Create save checkpoint in history and save to database
            flowEditor.value?.createSaveCheckpoint();

            // Get history from store and save to database (use new ID if changed)
            const currentId = newId || oldId;
            const automationFlowData = store.getters['flowEditor/getFlowById'](currentId, activeFlowIdentifier.value.type);
            if (automationFlowData?.history) {
              const automationHistory = [...(automationFlowData.history.undoStack || []), ...(automationFlowData.history.redoStack || [])];
              if (automationHistory.length > 0) {
                saveFlowHistory(TypeCampaignEnum.AUTOMATION, currentId, automationHistory).catch((historyError) => {
                  console.error('Failed to save flow history:', historyError);
                });
              }
            }

            await showToastSuccess(t('flows.save.success'));
            store.dispatch('flowEditor/markFlowAsSaved', { id: currentId, type: TypeCampaignEnum.AUTOMATION });
          } else {
            await showToastError(t('flows.save.error'));
          }
        } else {
          await showToastError(t('flows.save.error'));
        }
      } catch (error) {
        console.error('Error saving flow:', error);
        await showToastError(t('flows.save.error'));
      } finally {
        loading.value = false;
        if (closeAfterSave) {
          store.dispatch('flowEditor/closeFlow', activeFlowIdentifier.value);
        }
      }
    };

    const handleSave = async (closeAfterSave = false) => {
      if (!activeFlowIdentifier.value) return;

      // Check if flow has at least one operator
      if (!flow.value || !flowHasOperators(flow.value)) {
        await showToastError(t('flows.save.noOperators'));
        return;
      }

      // Validate settings before saving
      if (settingsPanelRef.value) {
        const validationResult = await settingsPanelRef.value.validate();
        if (!validationResult.isValid) {
          // Open settings panel and scroll to first error
          isSettingsPanelOpen.value = true;
          await settingsPanelRef.value.validateAndScrollToError();
          return;
        }
      }

      // Check for blocking warnings (only for bulk campaigns)
      const hasBlockingWarnings = flowRendererRef.value?.hasBlockingWarnings?.() ?? false;

      if (hasBlockingWarnings) {
        // Show confirmation dialog
        confirm.require({
          group: 'flowEditorConfirm',
          message: t('flows.save.blockingWarningsMessage'),
          header: t('flows.save.blockingWarningsHeader'),
          icon: 'far fa-exclamation-triangle',
          acceptLabel: t('flows.save.saveDraft'),
          rejectLabel: t('flows.save.fixProblems'),
          accept: () => {
            performSave(closeAfterSave);
          },
        });
        return;
      }

      await performSave(closeAfterSave);
    };

    const openScheduleModal = async () => {
      // Check if flow has at least one sending action
      if (!flow.value || !flowHasSendingAction(flow.value)) {
        await showToastError(t('flows.scheduleBulk.noSendingAction'));
        return;
      }

      // Check for blocking warnings
      const hasBlockingWarnings = flowRendererRef.value?.hasBlockingWarnings?.() ?? false;
      if (hasBlockingWarnings) {
        await showToastError(t('flows.scheduleBulk.hasBlockingWarnings'));
        return;
      }

      isScheduleModalOpen.value = true;
    };

    const closeScheduleModal = () => {
      isScheduleModalOpen.value = false;
    };

    interface ScheduleParams {
      send_type: 'later' | 'now';
      time_sending: string;
      timezone: string;
      perfect_timing: number;
      slowdown: number;
      interval_between_sends: string | null;
    }

    const handleSchedule = async (params: ScheduleParams) => {
      if (loading.value) return;

      if (!activeFlowIdentifier.value || !flow.value || !flowSettings.value) {
        await showToastError(t('flows.schedule.error'));
        return;
      }

      try {
        loading.value = true;

        // Step 1: Save the bulk campaign
        const saveResult = await saveBulk(flow.value, flowSettings.value);
        if (!saveResult.success) {
          await showToastError(t('flows.save.error'));
          return;
        }

        // Step 2: Schedule the bulk campaign
        await scheduleBulk({
          id: activeFlowIdentifier.value.id,
          time_sending: params.time_sending,
          perfect_timing: params.perfect_timing,
          slowdown: params.slowdown,
          interval_between_sends: params.interval_between_sends || '0',
          commercial_campaign: (flowSettings.value as FlowSettingsBulk).commercial_campaign ? 1 : 0,
        });

        await showToastSuccess(t('flows.schedule.success'));
        closeScheduleModal();

        // Step 3: Close the flow editor
        store.dispatch('flowEditor/closeFlow', activeFlowIdentifier.value);
      } catch (error) {
        console.error('Error scheduling campaign:', error);
        await showToastError(t('flows.schedule.error'));
      } finally {
        loading.value = false;
      }
    };

    // History panel functions
    const openHistoryPanel = () => {
      isHistoryPanelOpen.value = true;
    };

    const closeHistoryPanel = () => {
      isHistoryPanelOpen.value = false;
    };

    const handleHistoryRestore = (index: number) => {
      flowEditor.value?.restoreToIndex(index);
      closeHistoryPanel();
    };

    // Format history title for tooltip from meta
    const formatHistoryTitle = (baseKey: string, actionType?: string, meta?: HistoryEntryMeta, snapshot?: FlowStructureDto): string => {
      const base = t(`flows.history.${baseKey}`);
      if (actionType) {
        const actionTranslation = t(`flows.history.actions.${actionType}`);
        const description = formatHistoryMetaLabel(actionType, meta, snapshot);
        if (description) {
          return `${base}: ${actionTranslation} "${description}"`;
        }
        return `${base}: ${actionTranslation}`;
      }
      return `${base}`;
    };

    // Zoom state - synced with FlowRenderer
    const currentZoom = ref(1);

    const syncZoom = () => {
      nextTick(() => {
        const rendererZoom = flowRendererRef.value?.zoom;
        if (rendererZoom !== undefined) {
          currentZoom.value = rendererZoom;
        }
      });
    };

    // Zoom controls - delegate to FlowRenderer and sync zoom value
    const zoomIn = () => {
      flowRendererRef.value?.zoomIn();
      syncZoom();
    };

    const zoomOut = () => {
      flowRendererRef.value?.zoomOut();
      syncZoom();
    };

    const fitToScreen = () => {
      flowRendererRef.value?.fitToScreen();
      syncZoom();
    };

    // Content header toolbar START (left side: undo/redo, zoom)
    // In preview mode, hide undo/redo/history buttons
    const contentHeaderToolbarStart = computed(() => {
      const items: any[] = [];

      // Only show undo/redo/history in non-preview mode
      if (!isPreviewMode.value) {
        items.push({
          multipleType: 'buttons',
          items: [
            {
              icon: 'far fa-fw fa-undo',
              class: () => `${!canUndo.value && 'disabled'}`,
              tooltip: formatHistoryTitle('undo', undoActionType.value, undoMeta.value, undoSnapshot.value),
              tooltipPosition: 'right' as const,
              disabled: () => !canUndo.value,
              command: () => {
                if (canUndo.value) {
                  flowEditor.value?.undo();
                }
              },
            },
            {
              icon: 'far fa-fw fa-redo',
              class: () => `${!canRedo.value && 'disabled'}`,
              tooltip: formatHistoryTitle('redo', redoActionType.value, redoMeta.value, redoSnapshot.value),
              tooltipPosition: 'right' as const,
              disabled: () => !canRedo.value,
              command: () => {
                if (canRedo.value) {
                  flowEditor.value?.redo();
                }
              },
            },
            {
              icon: 'far fa-fw fa-clock-rotate-left',
              tooltip: t('flows.toolbar.history'),
              tooltipPosition: 'right' as const,
              command: openHistoryPanel,
            },
          ],
        });
      }

      // Zoom controls always visible
      items.push({
        multipleType: 'buttons',
        items: [
          {
            icon: 'far fa-fw fa-minus',
            tooltip: t('flows.toolbar.zoomOut'),
            command: zoomOut,
          },
          {
            label: `${Math.round(currentZoom.value * 100)}%`,
            class: 'zoom-label',
          },
          {
            icon: 'far fa-fw fa-plus',
            tooltip: t('flows.toolbar.zoomIn'),
            command: zoomIn,
          },
          {
            icon: 'far fa-fw fa-up-right-and-down-left-from-center',
            tooltip: t('flows.toolbar.fitToScreen'),
            command: fitToScreen,
          },
        ],
      });

      return items;
    });

    // Toggle analytics mode and load data from API
    const toggleAnalyticsMode = async () => {
      flowAnalytics.toggleAnalyticsMode();
      // Load analytics data when enabling analytics mode
      if (flowAnalytics.isAnalyticsMode.value) {
        const idShop = UserState.activeShop?.id ?? 0;
        const idCampaign = activeFlowIdentifier.value?.id ?? 0;
        const hasData = await flowAnalytics.loadAnalyticsData(
          idShop,
          activeFlowIdentifier.value.type,
          idCampaign,
        );
        if (!hasData) {
          showToastError(t('campaigns.common.operatorAnalytics.noAnalyticsForThisInterval'));
        }
      } else {
        // Close calendar picker when exiting analytics mode
        showCustomDatePicker.value = false;
      }
    };

    // Check if analytics button should be shown (only for automation with active or disabled status, or demo mode)
    const canShowAnalytics = computed(() => {
      if (!activeFlowIdentifier.value) return false;
      if (activeFlowIdentifier.value.type !== TypeCampaignEnum.AUTOMATION) return false;
      if (isDemoModeActive.value) return true;
      const status = activeFlow.value?.flowData?.status;
      return status === CampaignAutomationStatusEnum.ACTIVE
        || status === CampaignAutomationStatusEnum.DISABLED;
    });

    // Date interval options for analytics
    const dateIntervalOptions = computed(() => [
      { label: t('customDatePicker.lastDay'), value: IntervalDateEnum.LAST_DAY },
      { label: t('customDatePicker.last3Days'), value: IntervalDateEnum.LAST_3_DAYS },
      { label: t('customDatePicker.last7Days'), value: IntervalDateEnum.LAST_7_DAYS },
      { label: t('customDatePicker.last14Days'), value: IntervalDateEnum.LAST_14_DAYS },
      { label: t('customDatePicker.last30Days'), value: IntervalDateEnum.LAST_30_DAYS },
      { label: t('customDatePicker.last3Months'), value: IntervalDateEnum.LAST_3_MONTHS },
      { label: t('customDatePicker.last12Months'), value: IntervalDateEnum.LAST_12_MONTHS },
      { label: t('customDatePicker.customDateRange'), value: IntervalDateEnum.CUSTOM_DATE_RANGE },
    ]);

    // Handle date interval change
    const handleDateIntervalChange = async (interval: IntervalDateEnum) => {
      if (interval === IntervalDateEnum.CUSTOM_DATE_RANGE) {
        // Toggle calendar picker for custom date range
        showCustomDatePicker.value = !showCustomDatePicker.value;
      } else {
        showCustomDatePicker.value = false;
        flowAnalytics.setDateInterval(interval);
        // Reload analytics data with new interval
        const idShop = UserState.activeShop?.id ?? 0;
        const idCampaign = activeFlowIdentifier.value?.id ?? 0;
        const hasData = await flowAnalytics.loadAnalyticsData(
          idShop,
          activeFlowIdentifier.value.type,
          idCampaign,
        );
        if (!hasData) {
          showToastError(t('campaigns.common.operatorAnalytics.noAnalyticsForThisInterval'));
        }
      }
    };

    // Close custom date picker
    const closeCustomDatePicker = () => {
      showCustomDatePicker.value = false;
    };

    // Get current interval label for display
    const getCurrentIntervalLabel = () => {
      const current = flowAnalytics.dateInterval.value.interval;
      
      // If custom date range with dates selected, show the date range
      if (current === IntervalDateEnum.CUSTOM_DATE_RANGE) {
        const customDates = flowAnalytics.dateInterval.value.customDateRange;
        if (customDates && customDates.length === 2) {
          return `${formatDateShort(customDates[0])} - ${formatDateShort(customDates[1])}`;
        }
        return t('customDatePicker.customDateRange');
      }
      
      const option = dateIntervalOptions.value.find((opt) => opt.value === current);
      return option?.label || t('customDatePicker.last30Days');
    };

    // Content header toolbar END (right side: analytics group, settings)
    const contentHeaderToolbarEnd = computed(() => {
      const items: any[] = [];

      // Analytics group: date selector + toggle button grouped together
      if (flowAnalytics.isAnalyticsMode.value) {
        const analyticsGroupItems: any[] = [];

        // Date interval dropdown
        const dropdownItems = [
          {
            label: t('customDatePicker.empty'),
            class: 'text-color-secondary font-normal pointer-events-none',
            disabled: true,
          },
          { separator: true },
          ...dateIntervalOptions.value.map((opt) => ({
            label: opt.label,
            class: flowAnalytics.dateInterval.value.interval === opt.value
              ? 'font-bold bg-gray-100'
              : '',
            command: () => handleDateIntervalChange(opt.value),
          })),
        ];

        analyticsGroupItems.push({
          icon: 'far fa-fw fa-calendar-days',
          label: getCurrentIntervalLabel(),
          class: 'analytics-date-selector',
          items: dropdownItems,
        });

        // Analytics toggle button (only for automation, not for bulk/preview where it's always on)
        if (canShowAnalytics.value && !isPreviewMode.value) {
          analyticsGroupItems.push({
            icon: () => flowAnalytics.isLoading.value
              ? 'far fa-fw fa-spinner-third fa-spin'
              : 'far fa-fw fa-chart-line',
            tooltip: t('flows.toolbar.analytics'),
            class: () => flowAnalytics.isAnalyticsMode.value ? 'active' : '',
            disabled: () => flowAnalytics.isLoading.value,
            command: toggleAnalyticsMode,
          });
        }

        items.push({
          multipleType: 'buttons',
          items: analyticsGroupItems,
        });
      } else if (canShowAnalytics.value && !isPreviewMode.value) {
        // Analytics mode off: show only the toggle button (automation)
        items.push({
          multipleType: 'buttons',
          items: [
            {
              icon: 'far fa-fw fa-chart-line',
              tooltip: t('flows.toolbar.analytics'),
              command: toggleAnalyticsMode,
            },
          ],
        });
      }

      // Settings button - isolated, hidden in preview mode
      if (!isPreviewMode.value) {
        items.push({
          multipleType: 'buttons',
          items: [
            {
              icon: 'far fa-fw fa-gear',
              tooltip: t('flows.toolbar.settings'),
              command: openSettingsPanel,
            },
          ],
        });
      }

      return items;
    });

    const toolbarRightButtons = computed(() => {
      if (isPreviewMode.value) {
        return [
          {
            label: t('templateBuilder.close'),
            class: 'p-button-secondary',
            command: handleClose,
          },
        ];
      }

      const isBulk = activeFlowIdentifier.value?.type === TypeCampaignEnum.BULK;

      if (isBulk) {
        return [
          {
            label: t('automatedScenarios.headerSaveButton.save'),
            class: 'p-button-secondary',
            items: [
              {
                label: t('automatedScenarios.headerSaveButton.saveAndLeave'),
                command: () => handleSave(true),
              },
              {
                label: t('automatedScenarios.headerSaveButton.minimize'),
                command: handleMinimize,
              },
              {
                label: t('automatedScenarios.headerSaveButton.leaveWithoutSaving'),
                command: () => {
                  handleClose();
                },
              },
              {
                separator: true,
              },
              {
                label: t('automatedScenarios.headerSaveButton.scheduleCampaign'),
                command: openScheduleModal,
              },
              ...(isWorkshopAccount.value ? [
                { separator: true },
                {
                  label: t('workshop.renameTemplates.button'),
                  icon: 'far fa-fw fa-pen-to-square',
                  command: handleWorkshopRenameTemplates,
                },
              ] : []),
            ],
          },
        ];
      }

      // Automation
      return [
        {
          label: t('automatedScenarios.headerSaveButton.save'),
          class: 'p-button-secondary',
          items: [
            {
              label: t('automatedScenarios.headerSaveButton.saveAndLeave'),
              command: () => handleSave(true),
            },
            {
              label: t('automatedScenarios.headerSaveButton.save'),
              command: () => handleSave(false),
            },
            {
              label: t('automatedScenarios.headerSaveButton.minimize'),
              command: handleMinimize,
            },
            {
              label: t('automatedScenarios.headerSaveButton.leaveWithoutSaving'),
              command: () => {
                handleClose();
              },
            },
            ...(isWorkshopAccount.value ? [
              { separator: true },
              {
                label: t('workshop.renameTemplates.button'),
                icon: 'far fa-fw fa-pen-to-square',
                command: handleWorkshopRenameTemplates,
              },
            ] : []),
          ],
        },
      ];
    });

    // Workshop mode: batch rename all templates in the flow
    const handleWorkshopRenameTemplates = async () => {
      if (!flow.value) return;

      const idShop = UserState.activeShop?.id ?? 0;
      if (!idShop) return;

      const previews = await buildTemplateRenamePreview(flow.value, idShop, t);

      if (previews.length === 0) {
        showToastError(t('workshop.renameTemplates.noTemplates'));
        return;
      }

      const previewLines = previews.map(
        (p) => `${p.isRedirect ? '  ↳ ' : ''}#${p.templateId} → ${p.newName}`,
      ).join('\n');

      confirm.require({
        group: 'flowEditorConfirm',
        message: `${t('workshop.renameTemplates.confirmMessage')}\n\n${previewLines}`,
        header: t('workshop.renameTemplates.confirmHeader'),
        icon: 'far fa-fw fa-pen',
        acceptLabel: t('workshop.renameTemplates.confirm'),
        rejectLabel: t('workshop.renameTemplates.cancel'),
        accept: async () => {
          const result = await executeTemplateRenames(previews);
          if (result.success) {
            showToastSuccess(t('workshop.renameTemplates.success'));
          } else {
            showToastError(
              `${t('workshop.renameTemplates.partialError')}: ${result.errors.join(', ')}`,
            );
          }
        },
      });
    };

    const handleAddOperator = (payload: AddOperatorPayload) => {
      // Service verification is now done in FlowOperatorPanel before opening the form
      if (flowEditor.value) {
        flowEditor.value.addOperator(payload);
      }
    };

    const handleUpdateStep = (payload: { stepId: string; data: any }) => {
      if (flowEditor.value) {
        flowEditor.value.updateOperator(payload.stepId, payload.data);
      }
    };

    const handleDeleteStep = (payload: { stepId: string }) => {
      if (flowEditor.value) {
        flowEditor.value.removeOperator(payload.stepId);
      }
    };

    const handleDecreaseBranches = (payload: { stepId: string; outputsToRemove: string[] }) => {
      if (flowEditor.value) {
        flowEditor.value.removeBranches(payload.stepId, payload.outputsToRemove);
      }
    };

    const handleReplaceStep = (payload: { operator: any; data: any; stepId: string }) => {
      // Service verification is now done in FlowOperatorPanel before opening the form
      if (flowEditor.value) {
        flowEditor.value.replaceOperator(payload.stepId, payload.operator, payload.data);
      }
    };

    const handleMergeBranch = (payload: { sourceStepId: string; sourceOutput: number; targetStepId: string }) => {
      flowEditor.value?.addConnection(payload.sourceStepId, payload.sourceOutput, payload.targetStepId);
    };

    const handleReorderBranches = (payload: { stepId: string; newOrder: number[] }) => {
      flowEditor.value?.reorderBranches(payload.stepId, payload.newOrder);
    };

    const handleRemoveConnection = (payload: { parentId: string; parentOutput: number; childId: string }) => {
      flowEditor.value?.removeConnection(payload.parentId, payload.parentOutput, payload.childId);
    };

    const handleStartFlow = (type: FlowStructureType) => {
      if (flowRendererRef.value) {
        if (type === TypeCampaignEnum.BULK) {
          flowRendererRef.value.openTriggerForm('boxaudiencesetup');
        } else {
          flowRendererRef.value.openTriggerPanel();
        }
      }
    };

    // Send test message handling
    const showSendTestModal = ref(false);
    const isSendTestV4ModalVisible = computed(() => store.state.general.sendTestEmailV4ModalVisible);
    const sendTestTemplateType = ref('');
    const sendTestOperatorId = ref('');
    const sendTestStep = ref<any>(null);

    const handleMenuAction = async (actionId: string, step: any) => {
      const actionToTemplateType: Record<string, string> = {
        'send-test-email': 'email',
        'send-test-sms': 'sms',
        'send-test-whatsapp': 'whatsapp',
        'send-test-rcs': 'richtexting',
        'send-test-push': 'pushnotification',
      };

      const templateType = actionToTemplateType[actionId];
      if (templateType) {
        sendTestTemplateType.value = templateType;
        sendTestOperatorId.value = step.id;
        sendTestStep.value = step;

        if (templateType === 'email') {
          // Fetch template chain to check version (V3 vs V4)
          const templateId = step.parameters?.template_display_selector_id;
          let isV4 = false;
          if (templateId) {
            try {
              const response = await nestCall('get', NestApiType.CLIENT_API, `/templates/${templateId}/chain`, {}, '', undefined, true);
              if (response?.success && response.data?.length > 0) {
                isV4 = response.data[0].version === 4;
              }
            } catch (err) {
              // Fallback to legacy modal on error
            }
          }

          if (isV4 && templateId) {
            store.commit('general/showSendTestEmailV4Modal', templateId);
          } else {
            showSendTestModal.value = true;
            store.commit('general/showSendTestModal');
          }
        } else if (templateType === 'sms') {
          const templateId = step.parameters?.template_display_selector_id;
          if (templateId) {
            store.commit('general/showSendTestEmailV4Modal', templateId);
          } else {
            showSendTestModal.value = true;
            store.commit('general/showSendTestModal');
          }
        } else if (templateType === 'whatsapp') {
          const templateId = step.parameters?.template_display_selector_id;
          if (templateId) {
            store.commit('general/showSendTestEmailV4Modal', templateId);
          } else {
            showSendTestModal.value = true;
            store.commit('general/showSendTestModal');
          }
        } else if (templateType === 'richtexting') {
          const templateId = step.parameters?.template_display_selector_id;
          if (templateId) {
            store.commit('general/showSendTestEmailV4Modal', templateId);
          } else {
            showSendTestModal.value = true;
            store.commit('general/showSendTestModal');
          }
        } else {
          // Use the legacy modal for other types
          showSendTestModal.value = true;
          store.commit('general/showSendTestModal');
        }
      }
    };

    // Build send test request data from operator parameters
    const buildSendTestData = (
      params: any,
      contactId?: number | null,
      langs?: string[],
      conditionOverrides?: Record<string, boolean>,
    ) => {
      const operatorData = sendTestStep.value?.parameters || {};
      const getData: Record<string, any> = {
        type: sendTestTemplateType.value,
        params,
      };

      if (contactId) {
        getData.contactId = contactId;
      }
      if (Array.isArray(langs) && langs.length > 0) {
        getData.langs = langs;
      }
      if (conditionOverrides && Object.keys(conditionOverrides).length > 0) {
        getData.conditionOverrides = conditionOverrides;
      }

      if (['email', 'sms', 'whatsapp', 'richtexting'].includes(sendTestTemplateType.value)) {
        getData.informations = {
          fromName: operatorData.template_message_expname,
        };

        if (sendTestTemplateType.value === 'richtexting') {
          const rcsSenderId = operatorData.rcs_sender_id;
          if (rcsSenderId) {
            getData.informations.rcsSenderId = rcsSenderId;
            getData.senderId = rcsSenderId;
            getData.rcsSenderId = rcsSenderId;
          }
        }

        if (sendTestTemplateType.value === 'email') {
          getData.informations.fromEmail = operatorData.template_message_expmail;
          getData.informations.subject = operatorData.template_message_subject;
          getData.informations.previewText = operatorData.template_message_preview_text;
        }

        Object.keys(getData.informations).forEach((key) => {
          const val = getData.informations[key];
          if (typeof val === 'string') {
            try {
              getData.informations[key] = JSON.parse(val);
            } catch (e) {
              // Keep as string if parsing fails
            }
          }
        });
      }

      return getData;
    };

    // Execute the send test API call
    const executeSendTest = async (getData: Record<string, any>) => {
      const operatorData = sendTestStep.value?.parameters || {};
      const idShop = UserState.activeShop?.id ?? 0;

      const templateId = operatorData.template_display_selector_id;
      if (!templateId) {
        await showToastError(t('errorMessages.GENERIC_ERROR'));
        return;
      }

      const dataQuery = btoa(encodeURIComponent(JSON.stringify(getData)));
      const result = await nestCall(
        'get',
        NestApiType.DEFAULT,
        `/template/test/${idShop}/${templateId}/${dataQuery}`,
        {},
        '',
        {},
        false,
      );

      if (result && result.success) {
        if (sendTestTemplateType.value === 'email') {
          await showToastSuccess('emailSent');
        } else if (sendTestTemplateType.value === 'sms') {
          await showToastSuccess('smsSent');
        } else if (sendTestTemplateType.value === 'whatsapp') {
          await showToastSuccess('whatsappSent');
        } else if (sendTestTemplateType.value === 'richtexting') {
          await showToastSuccess('testSent');
        } else {
          await showToastSuccess('testSent');
        }
      } else {
        await showToastError(result && result.message ? t(`errorMessages.${result.message}`) : t('errorMessages.GENERIC_ERROR'));
      }
    };

    // Send test from legacy modal (SMS, WhatsApp, Push)
    const sendTestTemplate = async (params: any[]) => {
      if (!sendTestTemplateType.value || !sendTestStep.value) {
        showSendTestModal.value = false;
        return;
      }

      store.commit('general/showTheSpinner');

      try {
        const getData = buildSendTestData(params);
        await executeSendTest(getData);
      } catch (err: any) {
        await showToastError(err?.message ?? t('errorMessages.GENERIC_ERROR'));
      }

      store.commit('general/hideTheSpinner');
      sendTestTemplateType.value = '';
      sendTestOperatorId.value = '';
      sendTestStep.value = null;
      showSendTestModal.value = false;
    };

    // Send test from V4 modal (email with contact selection)
    const sendTestTemplateV4 = async (
      payload: {
        params: any;
        channel?: 'email' | 'sms' | 'whatsapp' | 'richtexting';
        contactId: number | null;
        langs?: string[];
        conditionOverrides?: Record<string, boolean>;
        senderId?: string;
        rcsSenderId?: string;
      },
    ) => {
      if (!sendTestTemplateType.value || !sendTestStep.value) {
        return;
      }

      store.commit('general/showTheSpinner');

      try {
        const getData = buildSendTestData(
          payload.params,
          payload.contactId,
          payload.langs,
          payload.conditionOverrides,
        );
        const resolvedRcsSenderId = payload.rcsSenderId || payload.senderId;
        if (sendTestTemplateType.value === 'richtexting' && resolvedRcsSenderId) {
          getData.senderId = resolvedRcsSenderId;
          getData.rcsSenderId = resolvedRcsSenderId;
          getData.informations = {
            ...(getData.informations || {}),
            rcsSenderId: resolvedRcsSenderId,
          };
        }
        await executeSendTest(getData);
      } catch (err: any) {
        await showToastError(err?.message ?? t('errorMessages.GENERIC_ERROR'));
      }

      store.commit('general/hideTheSpinner');
      sendTestTemplateType.value = '';
      sendTestOperatorId.value = '';
      sendTestStep.value = null;
    };

    const closeSendTestModal = () => {
      sendTestTemplateType.value = '';
      sendTestOperatorId.value = '';
      sendTestStep.value = null;
      showSendTestModal.value = false;
    };

    // Handle unload event - minimize active flow and save to localStorage
    const onEditorUnload = async () => {
      const activeIdentifier = store.getters['flowEditor/getActiveFlowIdentifier'];
      if (!activeIdentifier) return;

      // Reset status to DRAFT for active bulk campaign (skip in preview mode)
      if (activeIdentifier.type === TypeCampaignEnum.BULK && !isPreviewMode.value) {
        try {
          await updateBulkStatus(activeIdentifier.id, CampaignsBulkStatusEnum.DRAFT);
          store.commit('flowEditor/updateFlowStatus', {
            id: activeIdentifier.id,
            type: activeIdentifier.type,
            status: CampaignsBulkStatusEnum.DRAFT,
          });
        } catch (error) {
          console.error('Failed to update bulk status to DRAFT on unload:', error);
        }
      }

      // Minimize active flow to save to localStorage
      store.dispatch('flowEditor/minimizeFlow', activeIdentifier);
    };

    onMounted(() => {
      window.addEventListener('unload', onEditorUnload);
    });

    onBeforeUnmount(() => {
      window.removeEventListener('unload', onEditorUnload);
    });

    // Navigation tabs - filtered by current flow type (include minimized flows).
    // Hidden in preview mode: the bottom tab bar listing other open flows is
    // only relevant while editing.
    const navigationTabs = computed(() => {
      if (!activeFlowIdentifier.value) return [];
      if (isPreviewMode.value) return [];

      const currentType = activeFlowIdentifier.value.type;
      // Include all flows of same type (minimized or not)
      const flowsOfSameType = allFlows.value.filter(
        (f: any) => f.type === currentType,
      );

      if (flowsOfSameType.length === 0) return [];

      return flowsOfSameType.map((f: any) => {
        const isActive = String(f.id) === String(activeFlowIdentifier.value?.id)
          && f.type === activeFlowIdentifier.value?.type;
        const fullName = f.name || `Campagne n°${f.id}`;
        const truncated = truncateLabel(fullName, 25);
        return {
          label: truncated,
          tooltip: truncated !== fullName ? fullName : undefined,
          class: `${isActive ? 'p-button-success' : 'p-button-secondary'} footer-btn`,
          command: !isActive ? () => {
            // Restore if minimized, otherwise just switch
            if (f.isMinimized) {
              store.dispatch('flowEditor/restoreFlowWithConflictCheck', { id: f.id, type: f.type });
            } else {
              store.commit('flowEditor/setActiveFlow', { id: f.id, type: f.type });
            }
          } : undefined,
          items: [
            {
              label: t('templateBuilder.duplicate'),
              command: () => {
                console.log('Duplicate ==> ');
              },
            },
            {
              label: t('templateBuilder.close'),
              command: () => {
                handleClose();
              },
            },
          ],
        };
      });
    });

    return {
      t,
      flowRendererRef,
      loading,
      flow,
      flowType,
      modalTitle,
      toolbarRightButtons,
      contentHeaderToolbarStart,
      contentHeaderToolbarEnd,
      isPreviewMode,
      isBulkFlow,
      isCommercialActive,
      onCommercialSwitchChange,
      handleClose,
      handleSave,
      handleAddOperator,
      handleUpdateStep,
      handleDeleteStep,
      handleDecreaseBranches,
      handleReplaceStep,
      handleStartFlow,
      handleMenuAction,
      handleMergeBranch,
      handleReorderBranches,
      handleRemoveConnection,
      showSendTestModal,
      isSendTestV4ModalVisible,
      sendTestTemplateType,
      sendTestTemplate,
      sendTestTemplateV4,
      closeSendTestModal,
      navigationTabs,
      // Settings panel
      settingsPanelRef,
      isSettingsPanelOpen,
      flowSettings,
      activeFlowIdentifier,
      openSettingsPanel,
      closeSettingsPanel,
      // Schedule modal
      isScheduleModalOpen,
      closeScheduleModal,
      handleSchedule,
      // History panel
      isHistoryPanelOpen,
      undoStack,
      redoStack,
      closeHistoryPanel,
      handleHistoryRestore,
      // Analytics
      flowAnalytics,
      closeCustomDatePicker,
      customDateRange,
      showCustomDatePicker,
      onCustomDateSelect,
      IntervalDateEnum,
    };
  },
});
</script>

<style lang="scss" scoped>
.flow-editor-modal__content {
  position: relative;
  display: flex;
  flex-direction: column;
  height: calc(100vh - 6rem);
  background: #eef2f7;
}

.flow-editor-modal__state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  color: #4f5d75;
  font-size: 1rem;
}

.flow-editor-modal__state i {
  font-size: 1.4rem;
}

.flow-editor-modal__loading {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e8ed 100%);
}

.flow-editor-modal__loading-spinner {
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  border-radius: 50%;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);

  i {
    font-size: 2.5rem;
    color: $brand-color-primary;
  }
}

.flow-editor-modal__loading-text {
  font-size: 1.25rem;
  font-weight: 600;
  color: #2d3748;
  margin-top: 0.5rem;
}

.flow-editor-modal__loading-subtitle {
  font-size: 0.9rem;
  color: #718096;
}

// Analytics loading overlay
.flow-editor-modal__analytics-loading {
  position: absolute;
  inset: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(238, 242, 247, 0.6);
  backdrop-filter: blur(2px);
  pointer-events: none;
}

.flow-editor-modal__analytics-loading-pill {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1.25rem;
  background: white;
  border-radius: 2rem;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  font-size: 0.8125rem;
  font-weight: 500;
  color: #475569;

  i {
    color: $brand-color-primary;
    font-size: 0.875rem;
  }
}

// Fade transition
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.flow-editor-modal__custom-date-picker {
  position: absolute;
  top: 0.5rem;
  right: 1rem;
  z-index: 100;
  background: white;
  border-radius: 0.5rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  padding: 0.5rem;

  &-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.5rem 0.75rem;
    border-bottom: 1px solid #e2e8f0;
    margin-bottom: 0.5rem;

    span {
      font-size: 0.875rem;
      font-weight: 500;
      color: #64748b;
    }
  }

  &-close {
    background: none;
    border: none;
    cursor: pointer;
    padding: 0.25rem;
    color: #94a3b8;
    transition: color 0.2s;

    &:hover {
      color: #475569;
    }

    i {
      font-size: 1rem;
    }
  }
}
</style>

<style lang="scss" scoped>
// Commercial campaign toggle (bulk only) — rendered via the
// `content-header-end-extra` slot of <EditorModal>.
.commercial-toggle-wrapper {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0 0.75rem;

  &__label {
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
    cursor: pointer;
    user-select: none;
  }

  &__help {
    color: #9ca3af;
    cursor: help;
    font-size: 0.875rem;
  }

}

.commercial-readonly-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.25rem 0.65rem;
  margin: 0 0.5rem;
  border-radius: 999px;
  font-size: 0.8125rem;
  font-weight: 500;
  user-select: none;

  &--on {
    color: #15803d;
    background: rgba(34, 197, 94, 0.1);
    border: 1px solid rgba(34, 197, 94, 0.25);
  }

  &--off {
    color: #6b7280;
    background: rgba(148, 163, 184, 0.12);
    border: 1px solid rgba(148, 163, 184, 0.3);
  }

  &__help {
    color: #9ca3af;
    cursor: help;
    font-size: 0.8125rem;
    margin-left: 0.15rem;
  }
}
</style>
